/**
 * Сводный дашборд врача.
 * Агрегированная статистика по всем пациентам:
 * KPI-карточки, распределение трендов (круговая диаграмма),
 * топ-5 пациентов с наихудшим ИПС.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { doctorAPI } from '../../api/client';
import ChartExportWrapper from '../../components/ChartExportButton';

interface DashboardData {
  total_patients: number;
  trend_distribution: Record<string, number>;
  average_ips: number;
  patients_with_risks: number;
  risk_flags_summary: Record<string, number>;
  top_risk_patients: {
    id: number;
    name: string;
    ms_type: string | null;
    integrated_score: number;
    trend: string;
    trend_ru: string;
    risk_flags: string[];
    last_report_date: string | null;
  }[];
}

const TREND_COLORS: Record<string, string> = {
  improving: '#16a34a',
  stable: '#2563eb',
  worsening: '#f59e0b',
  critical: '#dc2626',
};

const TREND_LABELS: Record<string, string> = {
  improving: 'Улучшение',
  stable: 'Стабильно',
  worsening: 'Ухудшение',
  critical: 'Критично',
};

const RISK_LABELS: Record<string, string> = {
  high_score: 'Высокий ИПС',
  worsening_trend: 'Ухудшение тренда',
  extreme_symptom: 'Экстремальный симптом',
  missed_medication: 'Пропуск лекарств',
};

const DashboardPage: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anonymize, setAnonymize] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await doctorAPI.getDashboard();
        setData(res.data);
      } catch (err: any) {
        const msg = err.response?.data?.error || err.message || 'Неизвестная ошибка';
        console.error('Ошибка загрузки дашборда:', msg, err);
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <p>Загрузка...</p>;
  if (!data) return <p>Ошибка загрузки данных: {error || 'нет данных'}</p>;

  const pieData = Object.entries(data.trend_distribution)
    .filter(([, v]) => v > 0)
    .map(([key, value]) => ({
      name: TREND_LABELS[key] || key,
      value,
      color: TREND_COLORS[key] || '#6b7280',
    }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0 }}>Сводная панель</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={anonymize} onChange={e => setAnonymize(e.target.checked)} />
            Анонимизировать
          </label>
          <button
            style={{
              backgroundColor: '#2563eb', color: '#fff', border: 'none',
              padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
            }}
            onClick={async () => {
              let url: string | null = null;
              try {
                const res = await doctorAPI.downloadResearchExport(anonymize);
                url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a');
                a.href = url;
                a.download = 'research_export.csv';
                a.click();
              } catch (err) {
                console.error('Ошибка экспорта:', err);
              } finally {
                if (url) window.URL.revokeObjectURL(url);
              }
            }}
          >
            Экспорт для исследований
          </button>
        </div>
      </div>

      {/* KPI карточки */}
      <div style={styles.kpiGrid}>
        <div style={styles.kpiCard}>
          <div style={styles.kpiValue}>{data.total_patients}</div>
          <div style={styles.kpiLabel}>Всего пациентов</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiValue, color: '#2563eb' }}>{data.average_ips}</div>
          <div style={styles.kpiLabel}>Средний ИПС</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiValue, color: '#dc2626' }}>{data.patients_with_risks}</div>
          <div style={styles.kpiLabel}>С флагами риска</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiValue, color: '#16a34a' }}>
            {data.trend_distribution.improving || 0}
          </div>
          <div style={styles.kpiLabel}>Улучшение</div>
        </div>
      </div>

      {/* Два столбца: диаграмма + флаги риска */}
      <div style={styles.row}>
        {/* Круговая диаграмма трендов */}
        <div style={styles.chartCard}>
          <ChartExportWrapper fileName="trend_distribution">
            <h3 style={styles.cardTitle}>Распределение по трендам</h3>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%" cy="50%"
                    innerRadius={60} outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ color: 'var(--text-secondary)', textAlign: 'center', paddingTop: 40 }}>Нет данных</p>
            )}
          </ChartExportWrapper>
        </div>

        {/* Сводка флагов риска */}
        <div style={styles.chartCard}>
          <h3 style={styles.cardTitle}>Флаги риска</h3>
          <div style={{ padding: '12px 0' }}>
            {Object.entries(data.risk_flags_summary).map(([key, count]) => (
              <div key={key} style={styles.riskRow}>
                <span>{RISK_LABELS[key] || key}</span>
                <span style={{
                  ...styles.riskBadge,
                  backgroundColor: count > 0 ? '#fef2f2' : '#f0fdf4',
                  color: count > 0 ? '#dc2626' : '#16a34a',
                }}>
                  {count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Топ-5 пациентов */}
      <div style={styles.chartCard}>
        <h3 style={styles.cardTitle}>Пациенты, требующие внимания</h3>
        {data.top_risk_patients.length === 0 ? (
          <p style={{ color: '#6b7280' }}>Нет пациентов</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Пациент</th>
                <th style={styles.th}>Тип РС</th>
                <th style={styles.th}>ИПС</th>
                <th style={styles.th}>Тренд</th>
                <th style={styles.th}>Флаги</th>
                <th style={styles.th}>Посл. запись</th>
              </tr>
            </thead>
            <tbody>
              {data.top_risk_patients.map((p) => (
                <tr
                  key={p.id}
                  style={styles.tableRow}
                  onClick={() => navigate(`/doctor/patients/${p.id}`)}
                >
                  <td style={styles.td}>{p.name}</td>
                  <td style={styles.td}>{p.ms_type || '—'}</td>
                  <td style={{
                    ...styles.td,
                    fontWeight: 700,
                    color: p.integrated_score >= 7 ? '#dc2626' :
                           p.integrated_score >= 4 ? '#f59e0b' : '#16a34a',
                  }}>
                    {p.integrated_score}
                  </td>
                  <td style={{
                    ...styles.td,
                    color: TREND_COLORS[p.trend] || '#374151',
                  }}>
                    {p.trend_ru}
                  </td>
                  <td style={styles.td}>
                    {p.risk_flags.length > 0 ? (
                      <span style={{ color: '#dc2626', fontSize: 12 }}>
                        {p.risk_flags.length} флаг(ов)
                      </span>
                    ) : '—'}
                  </td>
                  <td style={styles.td}>{p.last_report_date || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 16,
    marginBottom: 24,
  },
  kpiCard: {
    backgroundColor: 'var(--bg-card)',
    padding: 20,
    borderRadius: 12,
    boxShadow: 'var(--shadow-card)',
    textAlign: 'center',
  },
  kpiValue: { fontSize: 32, fontWeight: 700, color: 'var(--text-primary)' },
  kpiLabel: { fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginBottom: 24,
  },
  chartCard: {
    backgroundColor: 'var(--bg-card)',
    padding: 20,
    borderRadius: 12,
    boxShadow: 'var(--shadow-card)',
    marginBottom: 16,
  },
  cardTitle: { fontSize: 16, marginBottom: 12, color: 'var(--text-primary)' },
  riskRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid var(--border-light)',
    fontSize: 14,
  },
  riskBadge: {
    padding: '4px 14px',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 600,
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    backgroundColor: 'var(--bg-table-header)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    borderBottom: '1px solid var(--border-color)',
  },
  td: {
    padding: '10px 12px',
    fontSize: 14,
    borderBottom: '1px solid var(--border-light)',
  },
  tableRow: { cursor: 'pointer' },
};

export default DashboardPage;
